// src/pages/DeliveriesOperation.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Input,
  Select,
  Card,
  Button,
  Tag,
  Space,
  Tooltip,
  Spin,
  Modal,
  Image,
  Empty,
  Tabs,
  Badge,
  message,
  Statistic,
  DatePicker,
  Checkbox,
} from "antd";
import {
  SearchOutlined,
  UserOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  InboxOutlined,
  TruckOutlined,
  StockOutlined,
  DownloadOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { useOrderContext } from "../contexts/OrderContext";

const { Option } = Select;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

// Helper functions
const getCompanyName = (rec) =>
  rec?.company_name ||
  rec?.customer_company ||
  rec?.customerCompany ||
  rec?.user_company ||
  rec?.user?.company_name ||
  rec?.user?.companyName ||
  rec?.customer?.company_name ||
  rec?.customer?.companyName ||
  "-";

const getStockStatus = (order) => {
  const stock = order.product?.stock;
  const quantity = order.quantity || 0;

  if (stock === null || stock === undefined) {
    return { status: "unknown", text: "Unknown", color: "default" };
  }

  if (stock >= quantity) {
    return { status: "in_stock", text: "In Stock", color: "success" };
  } else if (stock > 0) {
    return { status: "low_stock", text: `Low Stock (${stock})`, color: "warning" };
  } else {
    return { status: "out_of_stock", text: "Out of Stock", color: "error" };
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  try {
    const d = new Date(dateString);
    if (!isNaN(d.getTime())) {
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yy = String(d.getFullYear()).slice(-2);
      return `${dd}-${mm}-${yy}`;
    }
    return String(dateString);
  } catch {
    return String(dateString);
  }
};

const DeliveriesOperation = () => {
  const {
    orders,
    fetchOrders,
    scheduleOrders,
    updateDeliveryStatus,
    updateOrderStatus,
    deliveryStatuses,
  } = useOrderContext();

  // ✅ SALES READ-ONLY (no updates, no scheduling, no bulk actions)
  const userRole = (localStorage.getItem("userRole") || "").toLowerCase();
  const isSales = userRole === "sales" || userRole.includes("sales");

  const [productionOrders, setProductionOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [customerFilter, setCustomerFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [awaitingDispatchOrders, setAwaitingDispatchOrders] = useState(new Set());

  const rangePresets = [
    {
      label: "Last 7 days",
      value: [dayjs().subtract(7, "day").startOf("day"), dayjs().endOf("day")],
    },
    {
      label: "Last 30 days",
      value: [dayjs().subtract(30, "day").startOf("day"), dayjs().endOf("day")],
    },
    {
      label: "This month",
      value: [dayjs().startOf("month"), dayjs().endOf("month")],
    },
    {
      label: "Last month",
      value: [
        dayjs().subtract(1, "month").startOf("month"),
        dayjs().subtract(1, "month").endOf("month"),
      ],
    },
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      await Promise.all([
        fetchOrders(),
        axiosInstance
          .get("/admin/production/list", {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          })
          .then((productionResponse) => {
            const productionData =
              productionResponse.data.orders || productionResponse.data || [];
            setProductionOrders(productionData);
          }),
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const extractSheetNumber = (data) => {
    if (!data) return undefined;
    const candidates = [
      data.number,
      data.sheetNumber,
      data.sheet_no,
      data.sheetNo,
      data.id,
      data.orderSheetNumber,
      data?.sheet?.number,
      data?.sheet?.id,
    ];
    for (const v of candidates) {
      const n = Number(v);
      if (Number.isInteger(n) && n >= 0) return n;
    }
    return undefined;
  };

  const downloadProductionSheet = async (order) => {
    try {
      const token = localStorage.getItem("token");

      if (!order.isInProduction || order.productionStatus !== "shipped") {
        message.warning("This order is not available for production sheet download");
        return;
      }

      let osNumber;

      try {
        const { data } = await axiosInstance.post(
          "/admin/order-sheets/issue",
          { orderIds: [order.id] },
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        osNumber = extractSheetNumber(data);
        if (osNumber === undefined) {
          try {
            const peek = await axiosInstance.get("/admin/order-sheets/recent", {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            osNumber =
              extractSheetNumber(peek.data) ||
              extractSheetNumber(peek.data?.sheets?.[0]) ||
              Number(peek.data?.last);
          } catch {}
        }
      } catch (err) {
        message.warning(
          err?.response?.data?.message ||
            "Couldn't reserve an Order Sheet number. Exporting without a number…"
        );
      }

      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Order Sheet");

      const titleText =
        osNumber !== undefined && osNumber !== null ? `ORDER SHEET ${osNumber}` : "ORDER SHEET —";

      const titleRow = ws.addRow([titleText]);
      ws.mergeCells("A1:G1");
      titleRow.getCell(1).font = { bold: true, size: 16 };
      titleRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };

      ws.addRow([]);
      const header = ws.addRow([
        "Sr.No",
        "Product Name",
        "Color",
        "Size",
        "Quantity",
        "Price",
        "Storage Type",
      ]);
      header.font = { bold: true };
      header.alignment = { horizontal: "center", vertical: "middle" };
      header.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFBDD7EE" } };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      ws.columns = [
        { key: "sr_no", width: 8 },
        { key: "product_name", width: 30 },
        { key: "color", width: 15 },
        { key: "size", width: 12 },
        { key: "quantity", width: 10 },
        { key: "price", width: 12 },
        { key: "storageType", width: 15 },
      ];

      const cost = Number(order.factory_price || order.costPrice || 0);
      const totalCost = cost * Number(order.quantity || 0);

      const row = ws.addRow([
        1,
        order.product_name,
        order.color,
        order.size,
        order.quantity,
        cost,
        order.storageType,
      ]);
      row.eachCell((cell) => {
        cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      const totalRow = ws.addRow(["", "", "TOTAL", "", order.quantity, `£${totalCost.toFixed(2)}`, ""]);
      totalRow.font = { bold: true };
      totalRow.alignment = { horizontal: "center", vertical: "middle" };
      totalRow.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFBDD7EE" } };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      ws.addRow([]);

      const grandTotalRow = ws.addRow(["", "", "GRAND TOTAL", "", "", `£${totalCost.toFixed(2)}`, ""]);
      grandTotalRow.font = { bold: true, size: 14 };
      grandTotalRow.alignment = { horizontal: "center", vertical: "middle" };
      grandTotalRow.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFCC99" } };
        cell.border = {
          top: { style: "medium" },
          left: { style: "medium" },
          bottom: { style: "medium" },
          right: { style: "medium" },
        };
      });

      ws.addRow([]);

      const deliveryHeader = ws.addRow(["DELIVERY INFORMATION"]);
      ws.mergeCells(`A${deliveryHeader.number}:G${deliveryHeader.number}`);
      deliveryHeader.getCell(1).font = { bold: true, size: 14 };
      deliveryHeader.getCell(1).alignment = { horizontal: "center", vertical: "middle" };

      const deliveryInfo = [
        ["Customer:", getCompanyName(order)],
        ["Reference:", order.referenceNumber || "-"],
        ["Production Status:", order.productionStatus || "Not in Production"],
        ["Delivery Status:", order.deliveryStatus || "Pending"],
        ["Order Date:", formatDate(order.orderDate)],
        ...(order.trackingId ? [["Tracking ID:", order.trackingId]] : []),
        ...(order.orderNote ? [["Special Notes:", order.orderNote]] : []),
      ];

      deliveryInfo.forEach(([label, value]) => {
        const r = ws.addRow([label, value]);
        ws.mergeCells(`B${r.number}:G${r.number}`);
        r.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
        r.getCell(1).font = { bold: true };
      });

      const fileName =
        osNumber !== undefined && osNumber !== null
          ? `Order_Sheet_${osNumber}.xlsx`
          : `Order_Sheet_${order.referenceNumber || order.id}.xlsx`;

      const buf = await wb.xlsx.writeBuffer();
      saveAs(new Blob([buf]), fileName);
      message.success("Production sheet downloaded successfully!");
    } catch (error) {
      console.error("Error downloading production sheet:", error);
      message.error("Failed to download production sheet");
    }
  };

  // ✅ Combined Data + Sorted by Latest First
  const combinedData = useMemo(() => {
    const allOrders = [...(orders || [])];

    productionOrders.forEach((productionOrder) => {
      const existingOrderIndex = allOrders.findIndex((order) => order.id === productionOrder.id);
      if (existingOrderIndex !== -1) {
        allOrders[existingOrderIndex] = {
          ...allOrders[existingOrderIndex],
          productionStatus: productionOrder.sheetProductionStatus,
          isInProduction: true,
          color: productionOrder.color || allOrders[existingOrderIndex].color,
          size: productionOrder.size || allOrders[existingOrderIndex].size,
          storageType: productionOrder.storageType || allOrders[existingOrderIndex].storageType,
          factory_price: productionOrder.factory_price || allOrders[existingOrderIndex].factory_price,
          costPrice: productionOrder.costPrice || allOrders[existingOrderIndex].costPrice,
          fabric_name: productionOrder.fabric_name || allOrders[existingOrderIndex].fabric_name,
          BarcodeData: productionOrder.BarcodeData || allOrders[existingOrderIndex].BarcodeData,
          trackingId: productionOrder.trackingId || allOrders[existingOrderIndex].trackingId,
        };
      } else {
        allOrders.push({
          ...productionOrder,
          isInProduction: true,
          productionStatus: productionOrder.sheetProductionStatus,
        });
      }
    });

    return allOrders.sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate));
  }, [orders, productionOrders]);

  const filteredData = useMemo(() => {
    let data = [...combinedData];

    switch (activeTab) {
      case "in_stock":
        data = data.filter((order) => getStockStatus(order).status === "in_stock");
        break;
      case "in_production":
        data = data.filter((order) => order.isInProduction);
        break;
      case "shipped":
        data = data.filter(
          (order) => order.productionStatus === "shipped" || order.deliveryStatus === "order delivered"
        );
        break;
      default:
        break;
    }

    if (dateRange && dateRange[0] && dateRange[1]) {
      const [start, end] = dateRange;
      const startMs = start.startOf("day").valueOf();
      const endMs = end.endOf("day").valueOf();

      data = data.filter((order) => {
        const ms = dayjs(order.orderDate).valueOf();
        return ms >= startMs && ms <= endMs;
      });
    }

    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      data = data.filter(
        (order) =>
          (order.product_name || "").toLowerCase().includes(term) ||
          (order.referenceNumber || "").toLowerCase().includes(term) ||
          (order.trackingId || "").toLowerCase().includes(term) ||
          getCompanyName(order).toLowerCase().includes(term)
      );
    }

    if (customerFilter) {
      data = data.filter((order) => getCompanyName(order) === customerFilter);
    }

    return data;
  }, [combinedData, activeTab, searchTerm, customerFilter, statusFilter, dateRange]);

  const customerOptions = useMemo(() => {
    const counts = new Map();
    combinedData.forEach((order) => {
      const name = getCompanyName(order) || "-";
      counts.set(name, (counts.get(name) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count }));
  }, [combinedData]);

  const statistics = useMemo(() => {
    const total = combinedData.length;
    const inStock = combinedData.filter((order) => getStockStatus(order).status === "in_stock").length;
    const inProduction = combinedData.filter((order) => order.isInProduction).length;
    const shipped = combinedData.filter(
      (order) => order.productionStatus === "shipped" || order.deliveryStatus === "order delivered"
    ).length;

    return { total, inStock, inProduction, shipped };
  }, [combinedData]);

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setViewModalVisible(true);
  };

  const handleAwaitingDispatchChange = async (orderId, checked) => {
    if (isSales) {
      message.warning("Sales users can only view. Updates are disabled.");
      return;
    }
    try {
      if (checked) {
        await updateDeliveryStatus(orderId, "awaiting dispatch");
        setAwaitingDispatchOrders((prev) => new Set([...prev, orderId]));
        message.success("Order status updated to awaiting dispatch");
      } else {
        await updateDeliveryStatus(orderId, "scheduled");
        setAwaitingDispatchOrders((prev) => {
          const newSet = new Set(prev);
          newSet.delete(orderId);
          return newSet;
        });
        message.success("Order status updated to scheduled");
      }
      fetchData();
    } catch (error) {
      console.error("Error updating delivery status:", error);
      message.error(error?.response?.data?.message || "Failed to update delivery status");
    }
  };

  const handleOpenScheduleModal = () => {
    if (isSales) {
      message.warning("Sales users can only view. Scheduling is disabled.");
      return;
    }

    if (!selectedRowKeys.length) {
      message.warning("Select at least one order to schedule");
      return;
    }

    if (selectedRowKeys.length === 1) {
      const selected = filteredData.find((o) => o.id === selectedRowKeys[0]);
      if (selected?.deliveryStatus === "scheduled") {
        setSelectedOrder(selected);
      }
    }

    setScheduleModalVisible(true);
  };

  const handleConfirmSchedule = async () => {
    if (isSales) {
      message.warning("Sales users can only view. Scheduling is disabled.");
      return;
    }

    if (!scheduleDate) {
      message.error("Please select a schedule date");
      return;
    }
    try {
      await scheduleOrders(selectedRowKeys, scheduleDate);
      message.success("Orders scheduled successfully");
      setScheduleModalVisible(false);
      setScheduleDate(null);
      setSelectedRowKeys([]);
      fetchData();
    } catch (error) {
      console.error("Error scheduling orders:", error);
      message.error(error?.response?.data?.message || "Failed to schedule orders");
    }
  };

  const handleUpdateScheduleDate = async () => {
    if (isSales) {
      message.warning("Sales users can only view. Updates are disabled.");
      return;
    }

    if (!selectedOrder) {
      message.warning("No order selected");
      return;
    }
    if (!scheduleDate) {
      message.error("Please select a schedule date");
      return;
    }

    try {
      await axiosInstance.put(`/admin/orders/${selectedOrder.id}/update-schedule-date`, {
        scheduleDate,
      });

      message.success("Schedule date updated successfully");
      setScheduleModalVisible(false);
      setScheduleDate(null);
      setSelectedOrder(null);
      fetchData();
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to update schedule date");
    }
  };

  const handleBulkAwaitingDispatch = async () => {
    if (isSales) {
      message.warning("Sales users can only view. Updates are disabled.");
      return;
    }

    if (!selectedRowKeys.length) {
      message.warning("Please select orders to mark as awaiting dispatch");
      return;
    }

    try {
      const scheduledOrders = filteredData.filter(
        (order) => selectedRowKeys.includes(order.id) && order.deliveryStatus === "scheduled"
      );

      if (scheduledOrders.length === 0) {
        message.warning("No scheduled orders selected to move to awaiting dispatch");
        return;
      }

      for (const order of scheduledOrders) {
        await updateDeliveryStatus(order.id, "awaiting dispatch");
        setAwaitingDispatchOrders((prev) => new Set([...prev, order.id]));
      }

      message.success(`Updated ${scheduledOrders.length} orders to awaiting dispatch`);
      setSelectedRowKeys([]);
      fetchData();
    } catch (error) {
      console.error("Error bulk updating delivery status:", error);
      message.error("Failed to update orders to awaiting dispatch");
    }
  };

  // ✅ FIXED SR NO + FIXED DATE
  const columns = [
    {
      title: "ID",
      key: "srNo",
      width: 60,
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "Customer",
      key: "customer",
      width: 160,
      render: (_, record) => {
        const companyName = getCompanyName(record);
        const getInitials = (name) => {
          if (!name || name === "-") return "C";
          return name
            .split(" ")
            .map((word) => word.charAt(0))
            .join("")
            .toUpperCase()
            .slice(0, 2);
        };

        return (
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">
              {getInitials(companyName)}
            </div>
            <div className="text-sm font-medium text-gray-900 leading-tight min-w-0 flex-1">
              {companyName}
            </div>
          </div>
        );
      },
    },
    {
      title: "Product",
      dataIndex: "product_name",
      key: "product_name",
      width: 150,
      render: (text) => <div className="text-sm font-medium text-gray-900">{text}</div>,
    },
    {
      title: "Image",
      dataIndex: "product_image",
      key: "product_image",
      width: 80,
      render: (img) =>
        img ? (
          <Image
            src={img}
            width={50}
            height={50}
            style={{ objectFit: "cover", borderRadius: 6 }}
            fallback="https://via.placeholder.com/50"
            preview={false}
          />
        ) : (
          <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
            <span className="text-gray-400 text-xs">No Image</span>
          </div>
        ),
    },
    {
      title: "Date",
      dataIndex: "orderDate",
      key: "orderDate",
      width: 120,
      render: (_, record) => (
        <div className="text-sm text-gray-600 font-medium bg-gray-50 px-2 py-1 rounded text-center">
          {formatDate(record.orderDate)}
        </div>
      ),
    },
    {
      title: "Customer Ref",
      dataIndex: "referenceNumber",
      key: "referenceNumber",
      width: 120,
      render: (val) => (
        <div className="font-mono text-sm bg-gray-50 px-2 py-1 rounded border border-gray-200">
          {val || "-"}
        </div>
      ),
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: 70,
      render: (val) => <div className="text-center font-bold text-gray-700">{val}</div>,
    },
    {
      title: "Stock Status",
      key: "stock_status",
      width: 120,
      render: (_, record) => {
        const stockInfo = getStockStatus(record);
        const stockQuantity = record.product?.stock ?? "Unknown";

        return (
          <Tooltip title={`Available Stock: ${stockQuantity}`}>
            <Tag
              color={
                stockInfo.status === "in_stock"
                  ? "success"
                  : stockInfo.status === "low_stock"
                  ? "warning"
                  : stockInfo.status === "out_of_stock"
                  ? "error"
                  : "default"
              }
              className="text-xs font-medium w-full text-center"
            >
              {stockInfo.status === "low_stock" ? `${stockInfo.text}` : stockInfo.text}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Production Status",
      key: "production_status",
      width: 130,
      render: (_, record) => {
        const status = record.productionStatus || record.sheetProductionStatus;

        if (!record.isInProduction) {
          return (
            <Tag color="default" className="text-xs">
              Not in Production
            </Tag>
          );
        }

        let statusConfig =
          {
            "in progress": {
              color: "processing",
              icon: <ClockCircleOutlined />,
              text: "In Progress",
            },
            complete: {
              color: "success",
              icon: <CheckCircleOutlined />,
              text: "Complete",
            },
            shipped: {
              color: "purple",
              icon: <TruckOutlined />,
              text: "Shipped",
            },
          }[status] || { color: "default", text: status };

        return (
          <Tag color={statusConfig.color} icon={statusConfig.icon} className="text-xs font-medium">
            {statusConfig.text}
          </Tag>
        );
      },
    },
    {
      title: "Delivery Status",
      key: "delivery_status",
      width: 160,
      render: (_, record) => {
        const status = record.deliveryStatus || "pending";

        const getStatusColor = (s) => {
          switch (String(s || "").toLowerCase()) {
            case "scheduled":
              return "blue";
            case "awaiting dispatch":
              return "orange";
            case "order delivered":
              return "green";
            case "dispatched":
              return "purple";
            default:
              return "default";
          }
        };

        const formatStatusDate = (dateString) => {
          if (!dateString) return "";
          try {
            const date = new Date(dateString);
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            const year = String(date.getFullYear()).slice(-2);
            return `${month}/${day}/${year}`;
          } catch {
            return String(dateString);
          }
        };

        let displayDate = "";
        if (String(status).toLowerCase() === "scheduled") {
          displayDate = formatStatusDate(record.scheduleDate);
        } else if (String(status).toLowerCase() === "order delivered") {
          displayDate = formatStatusDate(record.deliveredAt);
        }

        return (
          <div className="text-center">
            <Tag color={getStatusColor(status)} className="text-xs font-medium w-full text-center mb-1">
              {status}
            </Tag>
            {displayDate && <div className="text-xs text-gray-600 font-medium">{displayDate}</div>}
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="primary"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
              className="bg-blue-500 hover:bg-blue-600 border-blue-500"
              size="small"
            />
          </Tooltip>

          {/* ✅ Sales can still download (does not update data). Remove this if you want SALES = view-only no downloads */}
          {record.isInProduction && record.productionStatus === "shipped" && (
            <Tooltip title="Download Production Sheet">
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => downloadProductionSheet(record)}
                className="bg-green-500 hover:bg-green-600 border-green-500"
                size="small"
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Spin spinning={loading} size="large" className="min-h-screen">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                  <TruckOutlined className="text-blue-600 text-2xl" />
                  Deliveries Operation
                </h1>
                <p className="text-gray-600">
                  Manage and track all orders with stock and production status
                </p>
              </div>

              <div className="flex items-center gap-4">
                <div className="bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-200">
                  <div className="flex items-center space-x-2 text-sm">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-gray-600">Total Orders:</span>
                    <span className="font-semibold text-gray-900">{statistics.total}</span>
                  </div>
                </div>

                {/* ✅ Sales should NOT see any update buttons */}
                {!isSales && (
                  <>
                    <Button
                      type="primary"
                      icon={<TruckOutlined />}
                      onClick={handleBulkAwaitingDispatch}
                      className="bg-orange-500 hover:bg-orange-600 border-orange-500 h-10 px-4"
                    >
                      Mark as Awaiting Dispatch
                    </Button>

                    <Button
                      type="primary"
                      icon={<ClockCircleOutlined />}
                      onClick={handleOpenScheduleModal}
                      className="bg-purple-500 hover:bg-purple-600 border-purple-500 h-10 px-4"
                    >
                      Schedule Orders
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            <Card className="border-0 shadow-sm rounded-2xl">
              <Statistic
                title="Total Orders"
                value={statistics.total}
                prefix={<InboxOutlined className="text-blue-500" />}
                valueStyle={{ color: "#3b82f6" }}
              />
            </Card>
            <Card className="border-0 shadow-sm rounded-2xl">
              <Statistic
                title="In Stock"
                value={statistics.inStock}
                prefix={<CheckCircleOutlined className="text-green-500" />}
                valueStyle={{ color: "#10b981" }}
              />
            </Card>
            <Card className="border-0 shadow-sm rounded-2xl">
              <Statistic
                title="In Production"
                value={statistics.inProduction}
                prefix={<ClockCircleOutlined className="text-orange-500" />}
                valueStyle={{ color: "#f59e0b" }}
              />
            </Card>
            <Card className="border-0 shadow-sm rounded-2xl">
              <Statistic
                title="Scheduled Orders"
                value={combinedData.filter((order) => order.deliveryStatus === "scheduled").length}
                prefix={<CalendarOutlined className="text-purple-500" />}
                valueStyle={{ color: "#8b5cf6" }}
              />
            </Card>
            <Card className="border-0 shadow-sm rounded-2xl">
              <Statistic
                title="Awaiting Dispatch"
                value={combinedData.filter((order) => order.deliveryStatus === "awaiting dispatch").length}
                prefix={<TruckOutlined className="text-orange-500" />}
                valueStyle={{ color: "#f97316" }}
              />
            </Card>
          </div>

          {/* Tabs Section */}
          <Card className="border-0 shadow-sm rounded-2xl mb-6">
            <Tabs activeKey={activeTab} onChange={setActiveTab} className="deliveries-tabs">
              <TabPane
                tab={
                  <span>
                    <InboxOutlined />
                    All Orders
                    <Badge count={statistics.total} style={{ backgroundColor: "#3b82f6", marginLeft: 8 }} />
                  </span>
                }
                key="all"
              />
              <TabPane
                tab={
                  <span>
                    <CheckCircleOutlined />
                    In Stock
                    <Badge count={statistics.inStock} style={{ backgroundColor: "#10b981", marginLeft: 8 }} />
                  </span>
                }
                key="in_stock"
              />
              <TabPane
                tab={
                  <span>
                    <ClockCircleOutlined />
                    In Production
                    <Badge count={statistics.inProduction} style={{ backgroundColor: "#f59e0b", marginLeft: 8 }} />
                  </span>
                }
                key="in_production"
              />
              <TabPane
                tab={
                  <span>
                    <TruckOutlined />
                    Shipped/Delivered
                    <Badge count={statistics.shipped} style={{ backgroundColor: "#8b5cf6", marginLeft: 8 }} />
                  </span>
                }
                key="shipped"
              />
            </Tabs>
          </Card>

          {/* Filters Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <SearchOutlined className="mr-2" />
                  Search Orders
                </label>
                <Input
                  placeholder="Search by product, reference, tracking..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  prefix={<SearchOutlined className="text-gray-400" />}
                  className="w-full h-10 rounded-lg"
                  allowClear
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <UserOutlined className="mr-2" />
                  Filter by Customer
                </label>
                <Select
                  placeholder="Select customer..."
                  value={customerFilter || undefined}
                  onChange={setCustomerFilter}
                  allowClear
                  className="w-full h-10 rounded-lg"
                  optionFilterProp="label"
                  showSearch
                >
                  {customerOptions.map(({ name, count }) => (
                    <Option key={name} value={name} label={name}>
                      <div className="flex justify-between items-center">
                        <span>{name}</span>
                        <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                          {count}
                        </span>
                      </div>
                    </Option>
                  ))}
                </Select>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <CalendarOutlined className="mr-2" />
                  Date Range
                </label>
                <div className="flex items-center gap-2">
                  <RangePicker
                    value={dateRange || undefined}
                    presets={rangePresets}
                    onChange={(vals) =>
                      setDateRange(
                        vals && vals[0] && vals[1] ? [vals[0].startOf("day"), vals[1].endOf("day")] : null
                      )
                    }
                    allowClear
                    className="w-full h-10"
                  />
                  <Button onClick={() => setDateRange(null)}>All</Button>
                </div>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <StockOutlined className="mr-2" />
                  Results
                </label>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 h-10 flex items-center">
                  <div className="text-sm font-medium text-gray-900">{filteredData.length} orders</div>
                </div>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <Table
              dataSource={filteredData}
              columns={columns}
              rowKey="id"
              // ✅ Sales cannot select rows (prevents bulk actions)
              rowSelection={
                isSales
                  ? undefined
                  : {
                      selectedRowKeys,
                      onChange: (keys) => setSelectedRowKeys(keys),
                    }
              }
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: filteredData.length,
                showSizeChanger: true,
                showQuickJumper: true,
                onChange: (page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                },
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                pageSizeOptions: ["10", "20", "50", "100"],
              }}
              scroll={{ x: "max-content" }}
              className="modern-orders-table"
             rowClassName={(record) => {
  const baseClasses = "hover:bg-gray-50 transition-colors duration-200";
  const stockInfo = getStockStatus(record);
  const statusText = String(record.status || record.deliveryStatus || "").toLowerCase();

  if (statusText === "cancelled" || statusText.includes("cancel")) {
    return `${baseClasses} cancelled-row`;
  }

  if (record.deliveryStatus === "order delivered") {
    return `${baseClasses} bg-green-50 border-l-4 border-l-green-400`;
  }

  if (stockInfo.status === "out_of_stock") {
    return `${baseClasses} bg-red-50 border-l-4 border-l-red-400`;
  }
  if (stockInfo.status === "low_stock") {
    return `${baseClasses} bg-orange-50 border-l-4 border-l-orange-400`;
  }
  if (record.productionStatus === "shipped") {
    return `${baseClasses} bg-purple-50 border-l-4 border-l-purple-400`;
  }
  return baseClasses;
}}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <div className="text-gray-500">
                        <div className="text-lg font-medium mb-2">No orders found</div>
                        <div className="text-sm">Try adjusting your search or filters</div>
                      </div>
                    }
                  />
                ),
              }}
            />
          </div>
        </div>
      </div>

      {/* Order Details Modal */}
      <Modal
        title={
          <div className="flex items-center space-x-3">
            <EyeOutlined className="text-blue-500 text-lg" />
            <div>
              <div className="text-lg font-semibold text-gray-900">Order Details</div>
              <div className="text-sm text-gray-500">Complete order information</div>
            </div>
          </div>
        }
        open={viewModalVisible}
        onCancel={() => {
          setViewModalVisible(false);
          setSelectedOrder(null);
        }}
        width={700}
        className="rounded-2xl"
        footer={[
          <Button
            key="close"
            onClick={() => {
              setViewModalVisible(false);
              setSelectedOrder(null);
            }}
            className="px-6 rounded-lg h-10 border-gray-300"
          >
            Close
          </Button>,

          // ✅ Sales cannot update schedule date
          ...(!isSales && selectedOrder?.deliveryStatus === "scheduled"
            ? [
                <Button
                  key="updateSchedule"
                  type="primary"
                  icon={<CalendarOutlined />}
                  onClick={() => setScheduleModalVisible(true)}
                  className="bg-purple-500 hover:bg-purple-600 border-purple-500 px-6 rounded-lg h-10"
                >
                  Update Schedule Date
                </Button>,
              ]
            : []),

          // ✅ Download sheet is allowed (does not update). If you want SALES = view-only no actions, wrap this with !isSales too.
          ...(selectedOrder?.isInProduction && selectedOrder?.productionStatus === "shipped"
            ? [
                <Button
                  key="download"
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => downloadProductionSheet(selectedOrder)}
                  className="bg-green-500 hover:bg-green-600 border-green-500 px-6 rounded-lg h-10"
                >
                  Download Sheet
                </Button>,
              ]
            : []),
        ]}
      >
        {selectedOrder && (
          <div className="mt-6 space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600">Customer</div>
                <div className="font-semibold text-gray-900">{getCompanyName(selectedOrder)}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600">Product Name</div>
                <div className="font-semibold text-gray-900">{selectedOrder.product_name}</div>
              </div>
            </div>

            {selectedOrder.product_image && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-2">Product Image</div>
                <Image
                  src={selectedOrder.product_image}
                  width={200}
                  height={140}
                  style={{ objectFit: "cover", borderRadius: 8 }}
                  preview={{ src: selectedOrder.product_image }}
                  className="border border-gray-200"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <div>
                  <div className="text-sm text-gray-600">Quantity</div>
                  <div className="font-semibold">{selectedOrder.quantity}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Order Date</div>
                  <div className="font-semibold">{formatDate(selectedOrder.orderDate)}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Reference No</div>
                  <div className="font-semibold">{selectedOrder.referenceNumber || "-"}</div>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="text-sm text-gray-600">Production Status</div>
                  <div className="font-semibold">
                    {selectedOrder.isInProduction ? (
                      <Tag
                        color={
                          selectedOrder.productionStatus === "in progress"
                            ? "processing"
                            : selectedOrder.productionStatus === "complete"
                            ? "success"
                            : selectedOrder.productionStatus === "shipped"
                            ? "purple"
                            : "default"
                        }
                      >
                        {selectedOrder.productionStatus || "Not Set"}
                      </Tag>
                    ) : (
                      <Tag color="default">Not in Production</Tag>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Delivery Status</div>
                  <div className="font-semibold">
                    <Tag
                      color={
                        selectedOrder.deliveryStatus === "order delivered"
                          ? "success"
                          : selectedOrder.deliveryStatus === "awaiting dispatch"
                          ? "orange"
                          : selectedOrder.deliveryStatus === "scheduled"
                          ? "blue"
                          : "processing"
                      }
                    >
                      {selectedOrder.deliveryStatus || "Pending"}
                    </Tag>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Priority</div>
                  <div className="font-semibold">
                    <Tag color={selectedOrder.priority === "urgent" ? "red" : "green"}>
                      {selectedOrder.priority || "normal"}
                    </Tag>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-2">Stock Status</div>
              <div className="font-medium">
                {(() => {
                  const stockInfo = getStockStatus(selectedOrder);
                  const stockQuantity = selectedOrder.product?.stock ?? "Unknown";
                  return (
                    <Tag
                      color={
                        stockInfo.status === "in_stock"
                          ? "success"
                          : stockInfo.status === "low_stock"
                          ? "warning"
                          : stockInfo.status === "out_of_stock"
                          ? "error"
                          : "default"
                      }
                    >
                      {stockInfo.text} {stockInfo.status === "low_stock" && `(Available: ${stockQuantity})`}
                    </Tag>
                  );
                })()}
              </div>
            </div>

            {selectedOrder.orderNote && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-2">Order Notes</div>
                <div className="font-medium text-gray-900">{selectedOrder.orderNote}</div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Schedule Orders Modal */}
      <Modal
        title={selectedOrder?.deliveryStatus === "scheduled" ? "Update Schedule Date" : "Schedule Orders"}
        open={scheduleModalVisible}
        onOk={selectedOrder?.deliveryStatus === "scheduled" ? handleUpdateScheduleDate : handleConfirmSchedule}
        onCancel={() => {
          setScheduleModalVisible(false);
          setScheduleDate(null);
          setSelectedOrder(null);
        }}
        okText={selectedOrder?.deliveryStatus === "scheduled" ? "Update Schedule" : "Confirm Schedule"}
        cancelText="Cancel"
        className="rounded-2xl"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            {selectedOrder?.deliveryStatus === "scheduled"
              ? "Update the scheduled delivery date:"
              : `Set a delivery schedule for ${selectedRowKeys.length} orders:`}
          </p>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Schedule Date</label>
            <DatePicker
              style={{ width: "100%" }}
              onChange={(d) => setScheduleDate(d)}
              className="w-full h-10 rounded-lg"
            />
          </div>
        </div>
      </Modal>

      <style jsx>{`
        .modern-orders-table .ant-table-thead > tr > th {
          background: #f8fafc;
          color: #374151;
          font-weight: 600;
          border-bottom: 2px solid #e5e7eb;
          white-space: nowrap;
          padding: 12px 16px;
        }

        .modern-orders-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9;
          padding: 12px 16px;
          transition: all 0.2s ease;
        }

        .deliveries-tabs .ant-tabs-tab {
          padding: 12px 16px;
          font-weight: 500;
        }

        .deliveries-tabs .ant-tabs-tab-active {
          background: #f8fafc;
          border-radius: 8px;
        }
          .modern-orders-table .ant-table-tbody > tr.cancelled-row > td {
  background: #fecaca !important;
}

.modern-orders-table .ant-table-tbody > tr.cancelled-row:hover > td {
  background: #fca5a5 !important;
}

.modern-orders-table .ant-table-tbody > tr.cancelled-row > td:first-child {
  border-left: 4px solid #dc2626;
}
      `}</style>
    </Spin>
  );
};

export default DeliveriesOperation;